import {
  useActionPrioritisation,
  type ActionPrioritisationRow,
} from '../../../lib/api/queries';

/**
 * Simple SVG Gantt chart mapping each remediation action to a 3-month
 * bucket derived from its criterion priority:
 *
 *  - High   → months 1-3   (dusty rose)
 *  - Medium → months 4-6   (warm sand)
 *  - Low    → months 7-12  (neutral grey)
 *
 * Rows sit in the same order as the Action Prioritisation table so the
 * two sections read together. Purely derived — any answer edit reflects
 * on the next load because it re-reads the prioritisation cache.
 */
export function ActionTimelineChart({
  tenantId, auditId,
}: { tenantId: string; auditId: string }) {
  const { data, isLoading } = useActionPrioritisation(tenantId, auditId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!data || data.rows.length === 0) {
    return (
      <p className="bg-success-50 border border-success-500/30 text-success-600
                    rounded-md px-4 py-3 text-sm">
        No remediation actions currently scheduled. Once findings are logged
        on the Form tab, they'll appear here on the timeline automatically.
      </p>
    );
  }

  // Layout constants — DXA-free, we're in SVG user units.
  const rowHeight    = 22;
  const rowGap       = 4;
  const labelWidth   = 260;    // left-hand label column
  const chartHeight  = data.rows.length * (rowHeight + rowGap) + 40;
  const chartWidth   = 780;
  const timelineX0   = labelWidth + 8;
  const timelineX1   = chartWidth - 12;
  const monthWidth   = (timelineX1 - timelineX0) / 12;
  const headerY      = 24;
  const rowY0        = headerY + 12;

  const bandFor = (priority: 'Low' | 'Medium' | 'High') => {
    switch (priority) {
      case 'High':   return { start: 1,  end: 3,  colour: '#C97878' };  // dusty rose
      case 'Medium': return { start: 4,  end: 6,  colour: '#E1B96B' };  // warm sand
      default:       return { start: 7,  end: 12, colour: '#9CA3AF' };  // grey
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-md p-4 overflow-x-auto">
      <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`}
           className="w-full"
           style={{ minWidth: 600 }}>
        {/* Month grid + labels */}
        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => {
          const x = timelineX0 + (m - 1) * monthWidth;
          return (
            <g key={m}>
              <line x1={x} y1={headerY} x2={x} y2={chartHeight - 8}
                    stroke="#E5E7EB" strokeWidth="1" />
              <text x={x + monthWidth / 2} y={headerY - 6}
                    fontSize="10" textAnchor="middle" fill="#6B7280">
                M{m}
              </text>
            </g>
          );
        })}
        {/* Right-hand chart edge */}
        <line x1={timelineX1} y1={headerY} x2={timelineX1} y2={chartHeight - 8}
              stroke="#E5E7EB" strokeWidth="1" />

        {/* Quarter dividers — subtle labels between the M lines */}
        {[3, 6, 9].map(cut => {
          const x = timelineX0 + cut * monthWidth;
          return <line key={cut} x1={x} y1={headerY - 2} x2={x} y2={chartHeight - 8}
                       stroke="#CBD5E1" strokeWidth="0.75" strokeDasharray="3 3" />;
        })}

        {/* Rows */}
        {data.rows.map((row, i) => {
          const y = rowY0 + i * (rowHeight + rowGap);
          const band = bandFor(row.priority);
          const barX = timelineX0 + (band.start - 1) * monthWidth;
          const barW = (band.end - band.start + 1) * monthWidth;

          const label = truncate(`${row.refNumber}. ${row.recommendedAction ?? row.itemText}`, 44);

          return (
            <g key={row.criterionItemId}>
              {/* Row background (zebra) */}
              <rect x={0} y={y - 2} width={chartWidth} height={rowHeight + 2}
                    fill={i % 2 === 0 ? '#FAFAFA' : 'transparent'} />
              {/* Label */}
              <text x={8} y={y + 14} fontSize="11" fill="#111827">
                {label}
              </text>
              {/* Bar */}
              <rect x={barX} y={y + 3} width={barW} height={rowHeight - 6}
                    fill={band.colour}
                    fillOpacity="0.85"
                    rx={3} ry={3}>
                <title>
                  {row.recommendedAction ?? row.itemText}
                  {` — ${row.priority} priority, months ${band.start}-${band.end}`}
                </title>
              </rect>
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
        <LegendSwatch colour="#C97878" label="High — months 1-3" />
        <LegendSwatch colour="#E1B96B" label="Medium — months 4-6" />
        <LegendSwatch colour="#9CA3AF" label="Low — months 7-12" />
      </div>
      <p className="text-[10px] text-gray-500 mt-1">
        Timeline derived from action priority. Reflects any change to scores
        on the Form tab.
      </p>

      {/* Per-quarter action tables — same source rows as the Gantt above,
          bucketed by priority so the tables mirror the coloured bars. */}
      <div className="mt-5 space-y-4">
        <BucketTable title="Remediation months 1-3"
                     rows={data.rows.filter(r => r.priority === 'High')}
                     accent="#C97878" />
        <BucketTable title="Remediation months 4-6"
                     rows={data.rows.filter(r => r.priority === 'Medium')}
                     accent="#E1B96B" />
        <BucketTable title="Remediation months 7-12"
                     rows={data.rows.filter(r => r.priority === 'Low')}
                     accent="#9CA3AF" />
      </div>
    </div>
  );
}

/**
 * Small remediation-months table shown under the Gantt. Empty buckets
 * render an "Ø No actions in this window" hint so the reader knows the
 * quarter is deliberately clear rather than accidentally missing.
 */
function BucketTable({
  title, rows, accent,
}: {
  title: string;
  rows: ActionPrioritisationRow[];
  accent: string;
}) {
  return (
    <section className="border border-gray-200 rounded-md overflow-hidden">
      <header className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
              style={{ backgroundColor: `${accent}1F`, color: accent }}>
        {title}
      </header>
      {rows.length === 0 ? (
        <p className="px-3 py-2 text-xs text-gray-500 italic">
          No actions scheduled in this window.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium w-14">Ref #</th>
              <th className="px-3 py-1.5 text-left font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(r => (
              <tr key={r.criterionItemId}>
                <td className="px-3 py-1.5 text-gray-500 tabular-nums align-top">
                  {r.refNumber}
                </td>
                <td className="px-3 py-1.5 text-gray-800">
                  {r.recommendedAction || r.itemText}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function LegendSwatch({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: colour }} />
      {label}
    </span>
  );
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1).trimEnd() + '…';
}
